/**
 * Models for the Create Email Template screen.
 *
 * ⚠️ API-shape assumption — cross-check against the Go backend before wiring the save call.
 * No template-create endpoint exists in `environment.apis` yet (only `viewtemplate`, which is the
 * read-only dropdown feed), so nothing here has been validated against a real backend struct. The
 * field names below follow `CampaignPayload`'s conventions (`messageTemplate`, `createdBy`, base64
 * attachments) so the eventual contract is a small step away rather than a rewrite.
 */

/** One uploaded image, carried inline as base64 — the same shape `csvattachments` uses. */
export interface TemplateImageAttachment {
  fileName: string;
  /** Base64 payload with the `data:` prefix stripped, as `CampaignPayload` sends it. */
  data: string;
  contentType: string;
}

/** An uploaded image plus the bits only the picker and the live preview need. */
export interface TemplateImage {
  attachment: TemplateImageAttachment;
  /** `data:image/png;base64,…` — used for the thumbnail and inside the preview document. */
  dataUrl: string;
  /** Human-readable size, e.g. `248 KB`. */
  size: string;
  mediaId?: number;
}

/** The two image slots a template has: one banner above the message, one below it. */
export type TemplateImageSlot = 'header' | 'footer';

/**
 * Content ids the composed HTML uses to reference each image slot.
 *
 * ⚠️ Assumption: the saved `messageTemplate` points at its images with `cid:` references — the
 * standard way an HTML email body addresses its own inline MIME attachments — and the backend
 * attaches `headerimage` / `footerimage` under these ids when it sends. The alternative is for the
 * backend to compose the banners around the body itself and ignore these references. Confirm which
 * before the save call is wired; the two produce different emails.
 */
export const TEMPLATE_IMAGE_CIDS: Record<TemplateImageSlot, string> = {
  header: 'headerimage',
  footer: 'footerimage'
};

/** Channel options for the template. Email-only for now; WhatsApp authoring is a separate ticket. */
export interface TemplateChannel {
  value: string;
  viewValue: string;
}

export const TEMPLATE_CHANNELS: TemplateChannel[] = [
  { value: 'email', viewValue: 'Email' },
  { value: 'whatsapp', viewValue: 'WhatsApp' },
  { value: 'sms', viewValue: 'SMS' }
];

/**
 * Request body for creating a template. Mirrors `CampaignPayload`: private fields with setters and
 * a `toString()` that the axios `data` property consumes directly.
 */
export class TemplatePayload {
  private name: string = '';
  private description: string = '';
  private channel: string = 'email';
  /** `yyyy-MM-dd`. ⚠️ Field names and format are assumptions — no backend struct to match yet. */
  private startDate: string = '';
  private endDate: string = '';
  private subject: string = '';
  /** The composed message as a complete HTML document — see `composeHtmlDocument()`. */
  private messageTemplate: string = '';
  private status: string = 'draft';
  private createdBy: number = 0;
  private headerimage: TemplateImageAttachment | null = null;
  private footerimage: TemplateImageAttachment | null = null;
  private id: number = 0;
  private templateSid: string = '';

  setName(name: string) { this.name = name; }
  setDescription(description: string) { this.description = description; }
  setChannel(channel: string) { this.channel = channel; }
  setStartDate(startDate: string) { this.startDate = startDate; }
  setEndDate(endDate: string) { this.endDate = endDate; }
  setSubject(subject: string) { this.subject = subject; }
  setMessageTemplate(messageTemplate: string) { this.messageTemplate = messageTemplate; }
  setStatus(status: string) { this.status = status; }
  setCreatedBy(id: number) { this.createdBy = id; }
  setHeaderImage(attachment: TemplateImageAttachment | null) { this.headerimage = attachment; }
  setFooterImage(attachment: TemplateImageAttachment | null) { this.footerimage = attachment; }
  setId(id: number) { this.id = id; }
  setTemplateSid(templateSid: string) { this.templateSid = templateSid; }

  toString(): string {
    return JSON.stringify({
      name: this.name,
      description: this.description,
      channel: this.channel,
      start_Date: this.startDate,
      end_Date: this.endDate,
      subject: this.subject,
      message_Template: this.messageTemplate,
      status: this.status,
      created_By: this.createdBy,
      headerimage: this.headerimage,
      footerimage: this.footerimage,
      id: this.id,
      template_sid: this.templateSid,
    });
  }
}

export interface TemplateImage {
  attachment: TemplateImageAttachment;
  dataUrl: string;
  size: string;
  file?: File;      // new
  s3Url?: string;   // new
}

export interface TemplateMediaUploadResponse {
  id: number;
  s3Url: string;
}
